import { useEffect, useState } from 'react'
import { Callout, StackList, toastError } from 'admination-react'
import {
  getSpalteStand,
  type DokumentSprachniveau,
  type SpalteStand,
  type SprachniveauAbweichung,
  type SprachniveauStand,
} from '../../api/spalteStand'
import type { UnterschriftDokument } from '../../api/unterschrift'

export const BOGEN_FEHLT = "Der Bewertungsbogen der Sprachlehrkraft fehlt noch."

const LADEN_FEHLGESCHLAGEN = "Der Abgleich des Sprachniveaus konnte nicht geladen werden."

const MELDUNG: Record<keyof SprachniveauStand, { titel: string; tun?: string }> = {
  foerderplan: { titel: "Der Förderplan ist veraltet: ein genanntes Sprachniveau stimmt nicht mehr mit seiner Quelle überein." },
  erstbericht: { titel: "Der Erstbericht ist veraltet: ein genanntes Sprachniveau stimmt nicht mehr mit seiner Quelle überein." },
  kompetenzbilanzierung: {
    titel: "Die Kompetenzbilanzierung nennt ein Sprachniveau, das keine Quelle belegt.",
    tun: "Nennen Sie im selben Satz die Quelle jedes Niveaus mit dem Niveau, das sie angibt, und speichern Sie die Kompetenzbilanzierung erneut.",
  },
}

export function useSprachniveauStand(coachingId: string | null, gespeichert: string | null): SpalteStand | null {
  const [stand, setStand] = useState<SpalteStand | null>(null)
  useEffect(() => {
    setStand(null)
    if (!coachingId || gespeichert === null) return
    let cancelled = false
    getSpalteStand(coachingId)
      .then((gelesen) => {
        if (!cancelled) setStand(gelesen)
      })
      .catch((error: unknown) => {
        console.error("Sprachniveau-Abgleich laden fehlgeschlagen:", error)
        if (!cancelled) toastError(LADEN_FEHLGESCHLAGEN)
      })
    return () => {
      cancelled = true
    }
  }, [coachingId, gespeichert])
  return stand
}

export function SprachniveauMarker({
  dokument,
  stand,
}: {
  dokument: keyof SprachniveauStand
  stand: DokumentSprachniveau | null | undefined
}) {
  if (!stand?.veraltet) return null
  const { titel, tun } = MELDUNG[dokument]
  return (
    <Callout tone="warning" role="status" data-testid={`sprachniveau-veraltet-${dokument}`} title={titel}>
      <StackList>
        {stand.hinweise.map((hinweis, i) => (
          <li key={`${i}-${hinweis}`}>{hinweis}</li>
        ))}
      </StackList>
      {tun}
    </Callout>
  )
}

const QUELLE = [
  ['sprachlehrkraft', "Bewertungsbogen Sprachlehrkraft"],
  ['klett', "Klett-Test"],
  ['aufnahme', "Aufnahme- und Potenzialanalyse"],
] as const satisfies readonly (readonly [keyof SprachniveauAbweichung, string])[]

export const ABWEICHUNG_TITEL = "Große Abweichung beim Sprachniveau"

export function abweichungText(abweichung: SprachniveauAbweichung): string {
  const niveaus = QUELLE.flatMap(([quelle, name]) => (abweichung[quelle] ? [`${name} ${abweichung[quelle]}`] : []))
  return `${niveaus.join(', ')}. Bitte klären Sie das Sprachniveau mit der Sprachlehrkraft.`
}

export function SprachniveauAbweichungHinweis({
  dokument,
  abweichung,
}: {
  dokument: UnterschriftDokument
  abweichung: SprachniveauAbweichung | null | undefined
}) {
  if (!abweichung) return null
  return (
    <Callout tone="error" role="alert" data-testid={`sprachniveau-abweichung-${dokument}`} title={ABWEICHUNG_TITEL}>
      {abweichungText(abweichung)}
    </Callout>
  )
}
